import OptionGroup from "lib/hex/views/components/OptionGroup";
import { registerForm } from "lib/hex/views/formRegistry";
import PropTypes from "prop-types";
import React from "react";
import Select from "lib/common/controls/Select";
import DateRange, {
    dateToString,
    isDateRangeValid,
} from "lib/hex/views/components/DateRange";
import useKeyValueState from "lib/hex/views/components/useKeyValueState";
import {
    fromCommaSafeArray,
    stringifyJson,
    toCommaSafeArray,
    unstringifyJson,
} from "lib/hex/views/components/serializeParams";

const FormView = ({onChange, state, data}) => (
    <div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Hospital</h3>
                <div className="form-group">
                    <div className="col-sm-12">
                        <Select
                            name="hospitals"
                            className="participant form-control"
                            onCommitChange={(v) => onChange("hospitals", v)}
                            onBlur={(v) => onChange("hospitalsBlur", state.hospitals)}
                            value={state.hospitals}
                            options={data.hospitals}
                            multiple={true}
                            required
                        />
                    </div>
                </div>
                <h5>Surgery Date Range</h5>
                <DateRange
                    start={state.start}
                    end={state.end}
                    min={data.daterange.from}
                    max={data.daterange.to}
                    onChange={onChange}
                    disabled={state.hospitals.length==0}
                    required
                />

            </div>
        </div>
        <div className="form-section form-horizontal">
            <div className="form-section-inner">
                <h3>Export Options</h3>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Field
                        Name Format</label>
                    <div className="col-md-7">
                        <Select
                            name="fieldnameFormat"
                            className="fieldnameFormat format_select form-control"
                            onCommitChange={(v) => onChange("fieldnameFormat", v)}
                            value={state.fieldnameFormat}
                            options={data.fieldnameFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor=""
                           className="col-md-5 control-label">Delimiter</label>
                    <div className="col-md-7">
                        <Select
                            name="delimiter"
                            className="delimiter format_select form-control"
                            onCommitChange={(v) => onChange("delimiter", v)}
                            value={state.delimiter}
                            options={data.delimiter}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Choice
                        Format</label>
                    <div className="col-md-7">
                        <Select
                            name="optionFormat"
                            className="optionFormat format_select form-control"
                            onCommitChange={(v) => onChange("optionFormat", v)}
                            value={state.optionFormat}
                            options={data.optionFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Date
                        Format</label>
                    <div className="col-md-7">
                        <Select
                            name="dateFormat"
                            className="dateFormat format_select form-control"
                            onCommitChange={(v) => onChange("dateFormat", v)}
                            value={state.dateFormat}
                            options={data.dateFormat}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="" className="col-md-5 control-label">Field
                        Sort</label>
                    <div className="col-md-7">
                        <Select
                            name="fieldSort"
                            className="dateFormat format_select form-control"
                            onCommitChange={(v) => onChange("fieldSort", v)}
                            value={state.fieldSort}
                            options={data.fieldSort}
                        />
                    </div>
                </div>

            </div>
            <OptionGroup
                state={state.exportFields}
                data={data.exportFields}
                expanded={false}
                onChange={(value) => onChange("exportFields", value)}
                hasFieldEntity={true}
            />
        </div>
    </div>
);

const selectShape = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })
);

FormView.propTypes = {
    onChange: PropTypes.func.isRequired,
    state: PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        hospitals: PropTypes.array,
        fieldnameFormat: PropTypes.string,
        delimiter: PropTypes.string,
        optionFormat: PropTypes.string,
        dateFormat: PropTypes.string,
        fieldSort: PropTypes.string,
        exportFields: PropTypes.object,
        hospitalsBlur: PropTypes.array,
        activeMembers: PropTypes.array,
    }),
    data: PropTypes.shape({
        fieldnameFormat: selectShape,
        delimiter: selectShape,
        optionFormat: selectShape,
        dateFormat: selectShape,
        fieldSort: selectShape,
        hospitals: selectShape,
        daterange: PropTypes.shape({
            from: PropTypes.array,
            to: PropTypes.array
        }),
        exportFields: PropTypes.array,
        activeMembers: selectShape,
    }),
};

const initialStateWithDefaults = (state = {}) => {
    const newState = {
        start: dateToString(state.start || ""),
        end: dateToString(state.end || ""),
        hospitals: state.hospitals || [],
        fieldnameFormat: state.fieldnameFormat || "SHORT",
        delimiter: state.delimiter || "|",
        optionFormat: state.optionFormat || "NAME",
        dateFormat: state.dateFormat || "MM/dd/yyyy",
        fieldSort: state.fieldSort || "FIELD",
        exportFields: state.exportFields || {},
        hospitalsBlur: state.hospitalsBlur || [],
    };
    return unstringifyJson(
        fromCommaSafeArray(newState),
        "exportFields",
    );
};

const serverDataParams = params =>
    stringifyJson(
        toCommaSafeArray(params),
        "exportFields",
    );

const initialData = {
    fieldnameFormat: [
        "SHORT",
        "LONG"
    ].map(v => ({label: v, value: v})),
    delimiter: [
        {value: "|", label: "|"},
        {value: "&#09;", label: "TAB"},
        {value: ",", label: ","},
    ],
    optionFormat: [
        "NAME",
        "HARVEST_VALUE",
    ].map(v => ({label: v, value: v})),
    dateFormat: [
        "MM/dd/yyyy",
        "yyyy-MM-dd",
        "dd/MM/yyyy",
    ].map(v => ({label: v, value: v})),
    fieldSort: [
        {
            label: "By Field Name",
            value: "FIELD"
        },
        {
            label: "By Section then Field Name",
            value: "SECTIONFIELD"
        }
    ],
    hospitals: [],
    daterange: {from: [], to: []},
    exportFields: [],
};

const ScoapExportForm = ({initialState, needServerData, onReady, onNotReady, activeMembers}) => {
    const [ state, setState ] = React.useState(() =>
        initialStateWithDefaults(initialState));
    React.useEffect(
        () => setState(initialStateWithDefaults(initialState)),
        [ initialState ],
    );
    const [ data, setDataKey ] = useKeyValueState(initialData);

    React.useEffect(() => {
        needServerData({
            endpoint: "/exportable/hospitals",
            loadingMessage: "Loading Hospitals...",
            onData: (data) => {
                data = data.map(it => ({
                    label: `${it.hospcode}: ${it.hospname}`,
                    value: it.hospcode,
                }));
                data.unshift({label: "All Hospitals", value: ""});
                setDataKey("hospitals", data);
            },
        });
    }, [activeMembers]);

    React.useEffect(() => {
        needServerData({
            endpoint: "/exportable/fields",
            loadingMessage: "Loading Available Fields...",
            onData: (data) => {
                const fields = data;
                if(_.isEmpty(state.exportFields)) {
                    // initially all fields are selected if none were provided.
                    const selected = {};
                    fields.forEach(it => {
                        it.fields.forEach(f => {
                            if(!selected[f.entity]) {
                                selected[f.entity] = [];
                            }
                            selected[f.entity].push(f.key);
                        });
                    });
                    handleChange("exportFields", selected);
                }
                setDataKey("exportFields", fields);
            }
        });
    }, []);

    React.useEffect(() => {
        const { hospitals, hospitalsBlur } = state;
        if (!hospitals || hospitals.length < 1) return;
        needServerData({
            endpoint: "/exportable/surgdt-range",
            loadingMessage: "Loading Available Date Range...",
            params: serverDataParams({
                hospitals,
            }),
            onData: (data) =>
                setDataKey("daterange", {from: data.from, to: data.to}),
        });
    }, [state.hospitalsBlur]);

    React.useEffect(() => {
        // update the submit button and data
        if (isDateRangeValid(state.start, state.end) && !!state.hospitals) {
            onReady(serverDataParams({
                ...state,
                exportFields: Object.entries(state.exportFields)
                    .reduce((agg, [ e, fs ]) => {
                        if (fs && fs.length) agg[e] = fs;
                        return agg;
                    }, {}),
            }));
        } else {
            onNotReady();
        }
    }, [ state, data ]);

    const handleChange = React.useCallback(
        (key, value) => setState(s => ({ ...s, [key]: value })),
        [ setState ],
    );

    return (
        <FormView
            state={state}
            data={data}
            onChange={handleChange}
        />
    );
};

ScoapExportForm.propTypes = {
    initialState: PropTypes.shape({
        start: PropTypes.any,
        end: PropTypes.any,
        hospitals: PropTypes.arrayOf(PropTypes.string),
        fieldnameFormat: PropTypes.string,
        delimiter: PropTypes.string,
        optionFormat: PropTypes.string,
        dateFormat: PropTypes.string,
        fieldSort: PropTypes.string,
        // history stores an object, saved params have JSON strings
        exportFields: PropTypes.oneOfType([ PropTypes.object, PropTypes.string ])
    }),
    needServerData: PropTypes.func.isRequired,
    onReady: PropTypes.func.isRequired,
    onNotReady: PropTypes.func.isRequired,
};

// ScoapExportForm.canRerun = params =>
//     params.hasOwnProperty("fieldnameFormat") &&
//     params.hasOwnProperty("delimiter") &&
//     params.hasOwnProperty("optionFormat") &&
//     params.hasOwnProperty("dateFormat") &&
//     params.hasOwnProperty("exportFields");

ScoapExportForm.canRerun = params => !!(params);

ScoapExportForm.apiPrefix = "scoap";

registerForm("scoap-export", ScoapExportForm);




// WEBPACK FOOTER //
// ./export_forms/api-scoap-2026.03.10-jar/hybrid-jsclient/ScoapExportForm.js